import React, { useCallback, useEffect } from 'react';
import { KendoReactTextArea } from '@Shared.Angular/kendo.react/KendoReactTextArea';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import { getService } from '@Client/runner.hooks/useService';
import { ISmartSearchResult } from '@Shared.Angular/@types/core/contracts/queryModel/smartSearch/smartSearchResult';
import { SmartSearchResult } from './SmartSearchResult';
import { Spinner } from '@Shared.Angular/flowingly.components/Spinner/Spinner';

type SmartSearchPanelProps = {
  onClose: () => void;
};

export const SmartSearchPanel = ({ onClose }: SmartSearchPanelProps) => {
  const smartSearchService = getService('smartSearchService');

  const [searchResults, setSearchResults] = React.useState<
    ISmartSearchResult[] | null
  >(smartSearchService.lastSearchResults);
  const [userInput, setUserInput] = React.useState<string>(
    smartSearchService.lastSearchTerm ?? ''
  );

  const focusTextArea = useCallback(() => {
    const textarea = document.querySelector<HTMLTextAreaElement>(
      '#smart-search-text-area'
    );
    textarea?.focus();
  }, []);

  useEffect(() => {
    focusTextArea();
  }, []);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const submitSearch = useCallback(async () => {
    if (!userInput.trim()) {
      return;
    }
    setIsLoading(true);
    setSearchResults(null);
    try {
      const results = await smartSearchService.getSearchResults(userInput);
      setSearchResults(results);
      document.querySelector('.smart-search-panel')?.scrollTo({ top: 0 });
    } catch {
      setSearchResults([]);
      getService('notificationService').showErrorToast(
        'An error occurred while searching. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [userInput]);

  const clearInput = (): void => {
    setUserInput('');
    focusTextArea();
  };

  const submitSearchOnEnterPress = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submitSearch();
      }
    },
    [submitSearch]
  );

  return (
    <div className={`smart-search-panel${isExpanded ? ' expanded' : ''}`}>
      <div className="smart-search-panel__header">
        <div className="smart-search-panel__header-title">
          <span className="fa-light fa-compass"> </span>Compass AI
        </div>
        <div className="smart-search-panel__header-actions">
          {!isExpanded && (
            <IconButton
              iconClass="fa-expand"
              title="Expand"
              onClick={() => {
                setIsExpanded(true);
                focusTextArea();
              }}
              border={false}
            />
          )}
          {isExpanded && (
            <IconButton
              iconClass="fa-compress"
              title="Collapse"
              onClick={() => {
                setIsExpanded(false);
                focusTextArea();
              }}
              border={false}
            />
          )}
          <IconButton
            iconClass="fa-x"
            title="Close"
            onClick={onClose}
            border={false}
          />
        </div>
      </div>
      <div className="flowingly-field">
        <KendoReactTextArea
          id="smart-search-text-area"
          rows={2}
          maxLength={4000}
          onKeyDown={submitSearchOnEnterPress}
          onChange={(e) => setUserInput(e.target.value)}
          value={userInput}
          disabled={isLoading}
          placeholder="Search SOPs, Maps or Workflows"
          prefix={
            <IconButton
              title="Search"
              onClick={submitSearch}
              iconClass="fa-magnifying-glass"
              border={false}
            />
          }
          suffix={
            userInput ? (
              <IconButton
                title="Clear search"
                onClick={clearInput}
                iconClass="fa-xmark"
                border={false}
              />
            ) : undefined
          }
        />
      </div>
      {isLoading && (
        <div className="smart-search-panel__loading">
          <Spinner size="small" />
          <div> Searching...</div>
        </div>
      )}
      {searchResults && searchResults.length === 0 && !isLoading && (
        <div className="smart-search-panel__no-results">No results found</div>
      )}
      {searchResults && searchResults.length > 0 && (
        <div className="smart-search-panel__results">
          {searchResults.map((result) => (
            <SmartSearchResult key={result.id} result={result} />
          ))}
        </div>
      )}
    </div>
  );
};
